'use client';

import { api } from '@/services/api';
import { CategoryProps } from '@/types/Category';
import { ProductProps } from '@/types/Product';
import {
  Breadcrumb,
  Flex,
  Grid,
  GridItem,
  IconButton,
  NativeSelect,
  Skeleton,
  Spinner,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import Link from 'next/link';
import { Fragment, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { isRayBanBrand, RayBanCertifiedStrip } from '@/components/RayBanCertified';
import { LuGrid2X2, LuList } from 'react-icons/lu';
import FilterButton from './FilterButton';
import ProductCard from '../ProductCard';
import { pushDataLayerEvent, toEcommerceItem } from '@/utils/dataLayer';

const PAGE_SIZE = 24;

type ViewMode = 'list' | 'grid';
const VIEW_MODE_STORAGE_KEY = 'ido:product-view-mode';

type ReturnSimple = {
  id: string;
  name: string;
  parent_id: string | null;
  slug: string;
  children: ReturnSimple[];
};

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Relevância' },
  { value: 'newest', label: 'Mais recentes' },
  { value: 'price_asc', label: 'Preço: mais baixo' },
  { value: 'price_desc', label: 'Preço: mais alto' },
];

type ListMode = 'category' | 'campaign' | 'bestsellers';

const ListProduct = ({
  categories,
  searchQuery,
  initialCategoriesSelected = [],
  initialProducts = [],
  initialTotal,
  mode = 'category',
  defaultSort = '',
  pageTitle,
  campaignTag,
  campaignColor,
}: {
  categories: string[];
  searchQuery?: string;
  initialCategoriesSelected?: CategoryProps[];
  initialProducts?: ProductProps[];
  /**
   * Total real de produtos correspondentes (vindo do `data.total` do fetch
   * SSR) — necessário porque `initialProducts` vem sempre limitado a uma
   * página (`limit=24`), por isso não pode ser usado para inferir o total.
   * Sem isto, a paginação nunca aparece na primeira visita a uma listagem
   * com mais de 24 resultados.
   */
  initialTotal?: number;
  /**
   * 'category' cobre tanto categorias como pesquisa/"todos os produtos"
   * (decidido por `categories.length === 0`, comportamento original), e
   * também "Novidades" (categories=[] + defaultSort="newest"). 'campaign'/
   * 'bestsellers' usam endpoints dedicados em vez de `/products/list/categories`.
   */
  mode?: ListMode;
  /** Ordenação inicial (ex: "newest" para a página de Novidades). */
  defaultSort?: string;
  /** Título fixo do cabeçalho — sobrepõe o título automático (categoria/pesquisa). */
  pageTitle?: string;
  /** Etiqueta da campanha a mostrar junto ao título (só faz sentido com mode="campaign"). */
  campaignTag?: string;
  campaignColor?: string;
}) => {
  const isGlobalSearch = categories.length === 0;
  const isCustomMode = mode === 'campaign' || mode === 'bestsellers';

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [filterUrl, setFilterUrl] = useState('');
  const [sort, setSort] = useState(defaultSort);
  const [page, setPage] = useState(1);

  // Vista em grelha/lista — só relevante em mobile (desktop usa sempre a
  // grelha multi-coluna responsiva). Default 'list' evita divergência entre
  // o HTML gerado no servidor e a 1ª renderização no cliente; a preferência
  // guardada é aplicada logo a seguir, já no cliente.
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  useEffect(() => {
    const stored = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    if (stored === 'grid' || stored === 'list') setViewMode(stored);
  }, []);

  const handleChangeViewMode = (mode: ViewMode) => {
    setViewMode(mode);
    try {
      window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
    } catch {
      // localStorage indisponível (modo privado, quota) — ignora, a
      // preferência simplesmente não persiste entre visitas.
    }
  };

  // O toggle só existe em mobile — no desktop a grelha multi-coluna já
  // mostra vários produtos por linha, não faz sentido "vista de lista".
  const isMobileViewport = useBreakpointValue({ base: true, md: false });
  const isCompactGrid = viewMode === 'grid' && !!isMobileViewport;

  const [products, setProducts] = useState<ProductProps[]>(initialProducts);
  const [total, setTotal] = useState(initialTotal ?? initialProducts.length);

  const [categoriesSelected, setCategoriesSelected] = useState<CategoryProps[]>(
    initialCategoriesSelected,
  );

  // SSR já trouxe a lista sem filtros — evita refazer o mesmo pedido no mount
  const skipNextProductFetch = useRef(
    isGlobalSearch || initialCategoriesSelected.length > 0,
  );

  // Identifica a "consulta" atual (tudo exceto a página) — usado para
  // distinguir "mudou o filtro/ordenação/categoria" (substitui a lista) de
  // "só avançou a página" (scroll infinito — acrescenta à lista existente).
  const fetchKeyRef = useRef('');
  // Identifica o pedido mais recente — respostas de pedidos já ultrapassados
  // (ex.: a página 1 a chegar depois da página 2) são ignoradas, senão
  // substituíam a lista e o scroll dava um salto.
  const requestIdRef = useRef(0);
  const hasMore = products.length < total;

  const showRayBanStrip =
    categoriesSelected[categoriesSelected.length - 1]?.slug === 'meta' ||
    (new URLSearchParams(filterUrl).get('marcas') ?? '')
      .split(',')
      .some((brand) => isRayBanBrand(brand));
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Produtos "brevemente" não têm preço público — ficam fora do tracking.
    const items = products
      .filter((p) => !p.coming_soon)
      .map((p) => toEcommerceItem(p));

    if (items.length === 0) return;

    pushDataLayerEvent('view_item_list', {
      ecommerce: {
        item_list_name:
          categoriesSelected[categoriesSelected.length - 1]?.name ||
          'Categoria',
        items,
      },
    });
  }, [products, categoriesSelected]);

  useEffect(() => {
    if (isGlobalSearch || categories.length === 0 || categoriesSelected.length > 0)
      return;

    const fetchCategories = async () => {
      setIsLoading(true);
      try {
        const response = await api.get<ReturnSimple[]>('/category');

        // Percorre a árvore nível a nível — cada segmento do URL tem de ser
        // filho direto do segmento anterior, não apenas existir algures na
        // árvore. Um segmento que não corresponda torna o caminho inválido
        // — pára logo, em vez de mostrar produtos de categorias que só
        // corresponderam parcialmente.
        let level = response.data;
        const matchedNodes: ReturnSimple[] = [];
        for (const slug of categories) {
          const node = level.find((n) => n.slug === slug);
          if (!node) {
            matchedNodes.length = 0;
            break;
          }
          matchedNodes.push(node);
          level = node.children ?? [];
        }

        const matched = matchedNodes.map((n) => ({
          id: n.id,
          name: n.name,
          parent_id: n.parent_id,
          slug: n.slug,
          children: Array.isArray(n.children) ? n.children : [],
        })) as unknown as CategoryProps[];
        setCategoriesSelected(matched);
      } catch {
        // silent
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, isGlobalSearch]);

  useEffect(() => {
    if (!isGlobalSearch && categoriesSelected.length === 0) return;

    if (
      skipNextProductFetch.current &&
      !filterUrl &&
      sort === defaultSort &&
      page === 1
    ) {
      skipNextProductFetch.current = false;
      fetchKeyRef.current = JSON.stringify([
        categoriesSelected.map((c) => c.id),
        filterUrl,
        sort,
        searchQuery,
        mode,
      ]);
      return;
    }
    skipNextProductFetch.current = false;

    // Tudo o que não seja a página — se mudar, é uma pesquisa nova (substitui
    // a lista); se for igual e a página avançou, é scroll infinito (acrescenta).
    const queryKey = JSON.stringify([
      categoriesSelected.map((c) => c.id),
      filterUrl,
      sort,
      searchQuery,
      mode,
    ]);
    const isLoadMore = queryKey === fetchKeyRef.current && page > 1;
    fetchKeyRef.current = queryKey;

    const requestId = ++requestIdRef.current;
    const isStale = () => requestId !== requestIdRef.current;

    const fetchProducts = async () => {
      if (isLoadMore) setIsLoadingMore(true);
      else setIsLoading(true);

      try {
        if (mode === 'bestsellers') {
          const response = await api.get(
            `/products/best-sellers?page=${page}&limit=${PAGE_SIZE}`,
          );
          if (isStale()) return;
          setProducts((prev) =>
            isLoadMore ? [...prev, ...response.data.items] : response.data.items,
          );
          setTotal(response.data.total);
          return;
        }

        const params = new URLSearchParams();
        if (categoriesSelected.length > 0) {
          // Só a categoria mais específica selecionada (não toda a cadeia
          // de antepassados) — senão uma subcategoria sem produtos próprios
          // herdava os produtos atribuídos diretamente à categoria-mãe.
          params.set(
            'categories',
            categoriesSelected[categoriesSelected.length - 1].id,
          );
        }
        if (searchQuery) params.set('q', searchQuery);
        if (sort) params.set('sort', sort);
        params.set('page', String(page));
        params.set('limit', String(PAGE_SIZE));

        const endpoint =
          mode === 'campaign' ? '/products/campaign' : '/products/list/categories';

        const response = await api.get(
          `${endpoint}?${params.toString()}${filterUrl ? `&${filterUrl}` : ''}`,
        );
        if (isStale()) return;
        setProducts((prev) =>
          isLoadMore ? [...prev, ...response.data.items] : response.data.items,
        );
        setTotal(response.data.total);
      } catch {
        // silent
      } finally {
        // Um pedido ultrapassado não mexe nos indicadores — o mais recente
        // é que os desliga quando terminar.
        if (!isStale()) {
          setIsLoadingMore(false);
          setIsLoading(false);
        }
      }
    };

    fetchProducts();

  }, [
    categoriesSelected,
    filterUrl,
    sort,
    page,
    searchQuery,
    isGlobalSearch,
    mode,
    defaultSort,
  ]);

  // Estável (useCallback): o FilterButton volta a aplicar os filtros sempre
  // que esta função muda. Antes era recriada a cada render — com um filtro
  // ativo, cada carregamento da página seguinte repunha a página em 1, e a
  // lista ficava em ciclo (página 1, 2, 1, 2…) no fim do scroll.
  const handleFilter = useCallback((url: string) => {
    setFilterUrl(url);
    setPage(1);
  }, []);

  // Scroll infinito: quando o sentinela (logo a seguir à grelha) entra na
  // área visível, avança a página — o efeito acima trata de acrescentar os
  // resultados. Recriar o observer quando o carregamento termina é
  // necessário para apanhar o caso em que o sentinela já está visível
  // (senão não há uma nova transição de visibilidade para o disparar).
  useEffect(() => {
    if (!hasMore || isLoading || isLoadingMore) return;
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setPage((p) => p + 1);
        }
      },
      { rootMargin: '600px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, isLoading, isLoadingMore]);

  return (
    <Flex width="100%" maxW="breakpoint-xl" flexDirection="column" pb="32px">
      {/* Header */}
      <Flex
        mt="16px"
        mb="20px"
        px={{ base: '12px', md: '0' }}
        align="center"
        justify="space-between"
        borderBottom="2px solid"
        borderColor="rgba(0,176,202,0.12)"
        pb="14px"
        flexWrap="wrap"
        gap="12px"
      >
        <Flex flexDir="column" gap="4px">
          <Flex align="center" gap="10px" flexWrap="wrap">
            <Text
              as="h1"
              fontWeight="800"
              fontSize="24px"
              color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
              letterSpacing="-0.3px"
            >
              {pageTitle
                ? pageTitle
                : isGlobalSearch
                  ? searchQuery
                    ? `Resultados para "${searchQuery}"`
                    : 'Todos os produtos'
                  : categoriesSelected[categoriesSelected.length - 1]?.name || 'Categoria'}
            </Text>
            {campaignTag && (
              <Flex
                px="10px"
                py="4px"
                borderRadius="full"
                bg={campaignColor || '#DC3232'}
              >
                <Text fontSize="12px" fontWeight="700" color="#fff">
                  {campaignTag}
                </Text>
              </Flex>
            )}
          </Flex>
          {!isGlobalSearch && (
            <Breadcrumb.Root>
              <Breadcrumb.List>
                <Breadcrumb.Item>
                  <Link href="/" style={{ color: '#00B0CA', fontSize: '13px' }}>
                    Home
                  </Link>
                </Breadcrumb.Item>
                <Breadcrumb.Separator />
                {categoriesSelected.map((category, index) => {
                  const href = `/categoria/${categories.slice(0, index + 1).join('/')}`;
                  if (index === categories.length - 1) {
                    return (
                      <Breadcrumb.Item key={category.id} fontWeight="600">
                        <Link href={href} style={{ fontSize: '13px' }}>{category.name}</Link>
                      </Breadcrumb.Item>
                    );
                  }
                  return (
                    <Fragment key={category.id}>
                      <Breadcrumb.Item>
                        <Link href={href} style={{ color: '#4E6E84', fontSize: '13px' }}>{category.name}</Link>
                      </Breadcrumb.Item>
                      <Breadcrumb.Separator />
                    </Fragment>
                  );
                })}
              </Breadcrumb.List>
            </Breadcrumb.Root>
          )}
          <Text fontSize="12px" color={{ base: '#6090A8', _dark: '#4E6E84' }}>
            {total} produto{total !== 1 ? 's' : ''} encontrado{total !== 1 ? 's' : ''}
          </Text>
        </Flex>

        {mode !== 'bestsellers' && (
          <Flex align="center" gap="10px">
            <NativeSelect.Root size="sm" w="180px">
              <NativeSelect.Field
                value={sort}
                onChange={(e) => {
                  setSort(e.currentTarget.value);
                  setPage(1);
                }}
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>

            {(isGlobalSearch || categoriesSelected.length > 0) && (
              <Suspense fallback={null}>
                <FilterButton
                  categories={categoriesSelected}
                  searchQuery={searchQuery}
                  handleFilter={handleFilter}
                />
              </Suspense>
            )}
          </Flex>
        )}
      </Flex>

      {/* Selo "Loja certificada Ray-Ban" — na categoria Meta (Ray-Ban Meta)
          e sempre que o filtro de marca inclui Ray-Ban. */}
      {showRayBanStrip && (
        <Flex px={{ base: '12px', md: '0' }} mb="16px">
          <RayBanCertifiedStrip />
        </Flex>
      )}

      {/* Products grid */}
      <Flex mt="4px" mb="2" flexDir="column" gap="20px" px={{ base: '12px', md: '0' }}>
        {/* Toggle grelha/lista — só em mobile */}
        <Flex display={{ base: 'flex', md: 'none' }} justify="flex-end">
          <Flex
            borderRadius="full"
            border="1px solid"
            borderColor={{ base: 'rgba(0,0,0,0.08)', _dark: 'rgba(255,255,255,0.1)' }}
            p="3px"
            gap="2px"
          >
            <IconButton
              aria-label="Vista em lista"
              size="xs"
              borderRadius="full"
              variant="ghost"
              bg={viewMode === 'list' ? '#00B0CA' : 'transparent'}
              color={viewMode === 'list' ? 'white' : { base: 'rgba(0,0,0,0.45)', _dark: 'rgba(255,255,255,0.45)' }}
              _hover={{ bg: viewMode === 'list' ? '#009AB5' : 'rgba(0,176,202,0.08)' }}
              onClick={() => handleChangeViewMode('list')}
            >
              <LuList />
            </IconButton>
            <IconButton
              aria-label="Vista em grelha"
              size="xs"
              borderRadius="full"
              variant="ghost"
              bg={viewMode === 'grid' ? '#00B0CA' : 'transparent'}
              color={viewMode === 'grid' ? 'white' : { base: 'rgba(0,0,0,0.45)', _dark: 'rgba(255,255,255,0.45)' }}
              _hover={{ bg: viewMode === 'grid' ? '#009AB5' : 'rgba(0,176,202,0.08)' }}
              onClick={() => handleChangeViewMode('grid')}
            >
              <LuGrid2X2 />
            </IconButton>
          </Flex>
        </Flex>

        <Grid
          templateColumns={{
            base: isCompactGrid ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(260px, 1fr))',
            md: 'repeat(auto-fill, minmax(260px, 1fr))',
          }}
          justifyItems="center"
          gap={{ base: isCompactGrid ? '10px' : '20px', md: '20px' }}
        >
          {isLoading
            ? Array.from({ length: 6 }).map((_, index) => (
                <GridItem key={index} w={isCompactGrid ? 'full' : 'auto'}>
                  <Skeleton
                    w={isCompactGrid ? 'full' : '260px'}
                    minH={isCompactGrid ? '260px' : '380px'}
                    borderRadius="20px"
                    bg={{ base: 'rgba(0,176,202,0.05)', _dark: '#0D2035' }}
                  />
                </GridItem>
              ))
            : products.map((product) => (
                <GridItem key={product.id} w={isCompactGrid ? 'full' : 'auto'}>
                  <ProductCard product={product} compact={isCompactGrid} />
                </GridItem>
              ))}
        </Grid>
        {products.length === 0 && !isLoading && (
          <Flex
            w="full"
            align="center"
            justify="center"
            py="64px"
            flexDir="column"
            gap="12px"
          >
            <Text fontSize="16px" color={{ base: '#4E6E84', _dark: '#6090A8' }} textAlign="center">
              {isCustomMode
                ? 'Nenhum produto encontrado.'
                : isGlobalSearch
                  ? 'Nenhum produto encontrado para a sua pesquisa.'
                  : 'Nenhum produto encontrado nesta categoria.'}
            </Text>
          </Flex>
        )}
        {/* Sentinela do scroll infinito — observado enquanto houver mais
            produtos para carregar; deixa de existir assim que tudo estiver
            carregado, para o rodapé continuar alcançável normalmente. */}
        {hasMore && !isLoading && (
          <Flex ref={sentinelRef} w="100%" align="center" justify="center" py="24px">
            {isLoadingMore && <Spinner size="md" color="#00B0CA" />}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default ListProduct;
